import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { usePermissions } from '@/hooks/usePermissions';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Eye, Search, FileText, Users, Settings } from 'lucide-react';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger, AlertDialogFooter, AlertDialogCancel } from '@/components/ui/alert-dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface SystemLog {
  id: string;
  table_name: string;
  operation: 'INSERT' | 'UPDATE' | 'DELETE';
  record_id?: string;
  old_data?: any;
  new_data?: any;
  changed_fields?: string[];
  user_id: string;
  user_email: string;
  timestamp: string;
}

export default function LogSistema() {
  const { hasPermission } = usePermissions();
  const [logs, setLogs] = useState<SystemLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [operationFilter, setOperationFilter] = useState<string>('all');
  const [tableFilter, setTableFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('timestamp');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    if (hasPermission('sistema', 'logs', 'read')) {
      fetchLogs();
    }
  }, [hasPermission]);

  const fetchLogs = async () => {
    try {
      let query = supabase
        .from('system_logs')
        .select('*');

      // Aplicar filtros
      if (operationFilter !== 'all') {
        query = query.eq('operation', operationFilter);
      }

      if (tableFilter !== 'all') {
        query = query.eq('table_name', tableFilter);
      }

      if (search) {
        query = query.or(`user_email.ilike.%${search}%,table_name.ilike.%${search}%`);
      }

      // Aplicar ordenação
      query = query.order(sortBy, { ascending: sortOrder === 'asc' });

      // Limitar resultados para performance
      query = query.limit(100);

      const { data, error } = await query;

      if (error) throw error;
      setLogs((data || []) as SystemLog[]);
    } catch (error) {
      console.error('Erro ao buscar logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getOperationBadge = (operation: string) => {
    const variants = {
      INSERT: 'default',
      UPDATE: 'secondary',
      DELETE: 'destructive'
    } as const;

    const colors = {
      INSERT: 'bg-green-100 text-green-800',
      UPDATE: 'bg-blue-100 text-blue-800',
      DELETE: 'bg-red-100 text-red-800'
    } as const;

    return (
      <Badge variant={variants[operation as keyof typeof variants]} className={colors[operation as keyof typeof colors]}>
        {operation}
      </Badge>
    );
  };

  const getTableIcon = (tableName: string) => {
    if (tableName.includes('user') || tableName.includes('profile')) return <Users className="w-4 h-4" />;
    if (tableName.includes('permission') || tableName.includes('role')) return <Settings className="w-4 h-4" />;
    return <FileText className="w-4 h-4" />;
  };

  const formatData = (data: any) => {
    if (!data) return 'N/A';
    return JSON.stringify(data, null, 2);
  };

  if (!hasPermission('sistema', 'logs', 'read')) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-muted-foreground">Você não tem permissão para acessar os logs do sistema.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Log do Sistema</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filtros e Busca</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por usuário ou tabela..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select value={operationFilter} onValueChange={setOperationFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filtrar por operação" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as operações</SelectItem>
                <SelectItem value="INSERT">Criação</SelectItem>
                <SelectItem value="UPDATE">Alteração</SelectItem>
                <SelectItem value="DELETE">Exclusão</SelectItem>
              </SelectContent>
            </Select>

            <Select value={tableFilter} onValueChange={setTableFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filtrar por tabela" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as tabelas</SelectItem>
                <SelectItem value="profiles">Perfis</SelectItem>
                <SelectItem value="permissions">Permissões</SelectItem>
                <SelectItem value="user_roles">Funções</SelectItem>
                
                <SelectItem value="contratacao">Contratação</SelectItem>
              </SelectContent>
            </Select>

            <Button onClick={fetchLogs} className="w-full">
              <Search className="w-4 h-4 mr-2" />
              Buscar
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Logs de Atividade</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Carregando logs...</div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead 
                      className="cursor-pointer hover:bg-muted"
                      onClick={() => {
                        setSortBy('timestamp');
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      }}
                    >
                      Data/Hora
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-muted"
                      onClick={() => {
                        setSortBy('user_email');
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      }}
                    >
                      Usuário
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-muted"
                      onClick={() => {
                        setSortBy('table_name');
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      }}
                    >
                      Tabela
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-muted"
                      onClick={() => {
                        setSortBy('operation');
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      }}
                    >
                      Operação
                    </TableHead>
                    <TableHead>Campos Alterados</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        {format(new Date(log.timestamp), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR })}
                      </TableCell>
                      <TableCell className="font-medium">{log.user_email}</TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          {getTableIcon(log.table_name)}
                          <span>{log.table_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>{getOperationBadge(log.operation)}</TableCell>
                      <TableCell>
                        {log.changed_fields && log.changed_fields.length > 0 
                          ? log.changed_fields.join(', ')
                          : '-'
                        }
                      </TableCell>
                      <TableCell>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm">
                              <Eye className="w-4 h-4 mr-2" />
                              Detalhes
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="max-w-4xl">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Detalhes do Log</AlertDialogTitle>
                            </AlertDialogHeader>
                            <ScrollArea className="max-h-96">
                              <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <h4 className="font-semibold mb-2">Informações Gerais</h4>
                                    <div className="space-y-1 text-sm">
                                      <p><strong>Usuário:</strong> {log.user_email}</p>
                                      <p><strong>Tabela:</strong> {log.table_name}</p>
                                      <p><strong>Operação:</strong> {log.operation}</p>
                                      <p><strong>Data:</strong> {format(new Date(log.timestamp), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR })}</p>
                                      {log.record_id && <p><strong>ID do Registro:</strong> {log.record_id}</p>}
                                    </div>
                                  </div>
                                  {log.changed_fields && log.changed_fields.length > 0 && (
                                    <div>
                                      <h4 className="font-semibold mb-2">Campos Alterados</h4>
                                      <div className="flex flex-wrap gap-1">
                                        {log.changed_fields.map((field, index) => (
                                          <Badge key={index} variant="outline">
                                            {field}
                                          </Badge>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {log.operation === 'UPDATE' && (
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                      <h4 className="font-semibold mb-2">Dados Anteriores</h4>
                                      <pre className="bg-muted p-3 rounded text-xs overflow-auto">
                                        {formatData(log.old_data)}
                                      </pre>
                                    </div>
                                    <div>
                                      <h4 className="font-semibold mb-2">Dados Novos</h4>
                                      <pre className="bg-muted p-3 rounded text-xs overflow-auto">
                                        {formatData(log.new_data)}
                                      </pre>
                                    </div>
                                  </div>
                                )}

                                {log.operation === 'INSERT' && log.new_data && (
                                  <div>
                                    <h4 className="font-semibold mb-2">Dados Inseridos</h4>
                                    <pre className="bg-muted p-3 rounded text-xs overflow-auto">
                                      {formatData(log.new_data)}
                                    </pre>
                                  </div>
                                )}

                                {log.operation === 'DELETE' && log.old_data && (
                                  <div>
                                    <h4 className="font-semibold mb-2">Dados Removidos</h4>
                                    <pre className="bg-muted p-3 rounded text-xs overflow-auto">
                                      {formatData(log.old_data)}
                                    </pre>
                                  </div>
                                )}
                              </div>
                            </ScrollArea>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Fechar</AlertDialogCancel>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {logs.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhum log encontrado com os filtros aplicados.
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}